// Lib
import React, { useEffect } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';

// Utils
import platformSingleton from '../../../../platform/platformSingleton';
import { isPlatformElectronMac } from '../../../../platform/utils/platformDetailsUtils';

// Components
import PopupPanel from '../../../../components/popupPanel/PopupPanel';
import AnchorButton from '../../../../components/buttons/AnchorButton';
import PopupPanelAsideSection from '../../../../components/popupPanel/types/asideSection/PopupPanelAsideSection';
import FriendlyTimestamp from '../../../../components/datetime/FriendlyTimestamp';
import Spinner from '../../../../components/loaders/Spinner';
import LoadableShortcutsModal from '../../../shortcuts/modal/LoadableShortcutsModal';

// Actions
import { intercomShowMessages, intercomShowHelpCenter } from '../../../../analytics/analyticsActions';
import { togglePopup } from '../../../../components/popupPanel/popupActions';
import { modalOpened } from '../../../../components/modal/modalActions';

// Constants
import { RELEASE_NOTES_WEB_URL, RELEASE_NOTES_MAC_URL } from '../../../../../common/utils/urlConstants';
import { ModalIds } from '../../../../components/modal/modalConstants';
import { PopupIds } from '../../../../components/popupPanel/popupConstants';

// Styles
import './HelpPopup.scss';

const RELEASE_NOTES_URL = isPlatformElectronMac(platformSingleton) ? RELEASE_NOTES_MAC_URL : RELEASE_NOTES_WEB_URL;

const parseReleaseDate = (releaseDate) => releaseDate && new Date(releaseDate).getTime();

const mapDispatchToProps = (dispatch) => ({
    handleHelpCenterButtonClick: () => {
        dispatch(togglePopup(PopupIds.HELP));
        dispatch(intercomShowHelpCenter());
    },
    handleFeedbackButtonClick: () => {
        dispatch(togglePopup(PopupIds.HELP));
        dispatch(intercomShowMessages());
    },
    handleShortcutsButtonClick: () => {
        dispatch(togglePopup(PopupIds.HELP));
        dispatch(modalOpened(ModalIds.SHORTCUTS));
    },
});

const HelpPopup = ({
    releaseInfo,
    handleHelpCenterButtonClick,
    handleFeedbackButtonClick,
    handleShortcutsButtonClick,
    fetchReleaseInfo,
    ...props
}) => {
    useEffect(() => {
        fetchReleaseInfo();
        LoadableShortcutsModal.preload();
    }, []);

    return (
        <>
            <PopupPanelAsideSection
                wrapper={<AnchorButton onClickFn={handleHelpCenterButtonClick} />}
                asideChildren={<img className="help" src={require('./help.png')} />}
            >
                <h3>Search our help center</h3>
                <p>Read guides, articles & documents</p>
            </PopupPanelAsideSection>
            <PopupPanelAsideSection
                wrapper={<AnchorButton onClickFn={handleFeedbackButtonClick} />}
                asideChildren={
                    <div className="icon">
                        <img className="chat-head" src={require('./icon-help-centre.png')} />
                    </div>
                }
            >
                <h3>Message our support team</h3>
                <p>We’ll respond in a few hours</p>
            </PopupPanelAsideSection>
            <PopupPanelAsideSection
                wrapper={<AnchorButton onClickFn={handleShortcutsButtonClick} />}
                asideChildren={<img className="shortcuts" src={require('./shortcuts.png')} />}
            >
                <h3>Shortcuts</h3>
                <p>Learn the quick way to perform common tasks</p>
            </PopupPanelAsideSection>
            <PopupPanelAsideSection
                wrapper={<AnchorButton href={RELEASE_NOTES_URL} target="_blank" />}
                asideChildren={<img className="release" src={require('./release-notes.png')} />}
            >
                <h3>Find out what&apos;s new</h3>
                {releaseInfo ? (
                    <div className="release-info">
                        Last updated <FriendlyTimestamp timestamp={parseReleaseDate(releaseInfo.date)} showSuffix />
                    </div>
                ) : (
                    <div className="release-info">
                        <Spinner show />
                    </div>
                )}
            </PopupPanelAsideSection>
        </>
    );
};

HelpPopup.propTypes = {
    unreadMessageCount: PropTypes.number,
    releaseInfo: PropTypes.object,
    handleHelpCenterButtonClick: PropTypes.func,
    handleFeedbackButtonClick: PropTypes.func,
    handleShortcutsButtonClick: PropTypes.func,
    fetchReleaseInfo: PropTypes.func,
};

const HelpPopupPanelContainer = (props) => (
    <PopupPanel {...props} popupId={PopupIds.HELP} className="HelpPopup">
        <HelpPopup {...props} />
    </PopupPanel>
);

export default connect(null, mapDispatchToProps)(HelpPopupPanelContainer);
